import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { SimpleTable } from './K2Tables';
import { fmtCz, fmtKc } from './k2utils';
import { fmtQty } from './itemsUtils';

const COLUMNS = [
  { key: 'date', label: 'Datum', render: (r) => fmtCz(r.date), className: 'whitespace-nowrap' },
  { key: 'doc', label: 'Doklad', className: 'font-medium whitespace-nowrap' },
  { key: 'book', label: 'Řada' },
  { key: 'warehouse', label: 'Sklad' },
  { key: 'description', label: 'Popis', className: 'text-[#57534E]' },
  { key: 'quantity', label: 'Množství', num: true, render: (r) => <span className={r.quantity < 0 ? 'text-[#16A34A]' : ''}>{fmtQty(r.quantity, r.unit)}</span> },
  { key: 'value', label: 'Hodnota', num: true, render: (r) => <span className={r.value < 0 ? 'text-[#16A34A]' : 'font-semibold'}>{fmtKc(r.value)}</span> },
];

function byMonth(lines, metric) {
  const map = new Map();
  [...lines].reverse().forEach((x) => {
    const k = x.date.slice(0, 7);
    if (!map.has(k)) map.set(k, { label: format(parseISO(`${k}-01`), 'MM/yyyy'), b290: 0, b291: 0 });
    map.get(k)[x.book === '290' ? 'b290' : 'b291'] += metric === 'qty' ? x.quantity : x.value;
  });
  return [...map.values()];
}

const Box = ({ label, value, cls, testId }) => (
  <div className={`p-3 rounded-xl ${cls}`} data-testid={testId}>
    <div className="text-xs text-[#57534E]">{label}</div>
    <div className="font-bold">{value}</div>
  </div>
);

const Summary = ({ lines }) => {
  const unit = lines[0]?.unit;
  const pos = lines.filter((x) => x.quantity >= 0);
  const neg = lines.filter((x) => x.quantity < 0);
  const s = (arr, k) => arr.reduce((a, x) => a + x[k], 0);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
      <Box label="Odepsáno celkem" value={`${fmtQty(s(pos, 'quantity'), unit)} · ${fmtKc(s(pos, 'value'))}`} cls="bg-[#FEF2F2] text-[#DC2626]" testId="k2-hist-written" />
      <Box label="Přebytek celkem" value={`${fmtQty(-s(neg, 'quantity'), unit)} · ${fmtKc(-s(neg, 'value'))}`} cls="bg-[#F0FDF4] text-[#16A34A]" testId="k2-hist-surplus" />
      <Box label="Čistě" value={`${fmtQty(s(lines, 'quantity'), unit)} · ${fmtKc(s(lines, 'value'))}`} cls="bg-[#F8FAF8] text-[#052E16]" testId="k2-hist-net" />
      <Box label="Dokladů · období" value={`${new Set(lines.map((x) => x.doc_rid)).size} · ${fmtCz(lines[lines.length - 1].date)} – ${fmtCz(lines[0].date)}`} cls="bg-[#F8FAF8] text-[#052E16]" testId="k2-hist-docs" />
    </div>
  );
};

export const K2ArticleHistory = ({ article, onClose }) => {
  const [state, setState] = useState({ lines: [], loading: false, error: null });
  const [metric, setMetric] = useState('value');
  useEffect(() => {
    if (!article) return undefined;
    let alive = true;
    setState({ lines: [], loading: true, error: null });
    axios.get('/api/k2/article-history', { params: { code: article.code }, withCredentials: true })
      .then((r) => alive && setState({ lines: r.data.items, loading: false, error: null }))
      .catch((e) => alive && setState({ lines: [], loading: false, error: e.response?.data?.detail || 'Nepodařilo se načíst historii z K2' }));
    return () => { alive = false; };
  }, [article]);
  const chart = useMemo(() => byMonth(state.lines, metric), [state.lines, metric]);
  const unit = state.lines[0]?.unit;

  return (
    <Dialog open={!!article} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="k2-article-history">
        {article && (
          <>
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] text-[#052E16]" data-testid="k2-hist-title">
                Historie odpisů: {article.name} <span className="text-sm font-normal text-[#57534E]">({article.code})</span>
              </DialogTitle>
            </DialogHeader>
            {state.loading && (
              <div className="py-12 flex items-center justify-center gap-2 text-sm text-[#57534E]" data-testid="k2-hist-loading">
                <Loader2 className="w-5 h-5 animate-spin text-[#16A34A]" /> Načítám historii z K2…
              </div>
            )}
            {state.error && <div className="p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="k2-hist-error">{state.error}</div>}
            {!state.loading && !state.error && state.lines.length === 0 && (
              <p className="py-8 text-center text-[#94A3B8]" data-testid="k2-hist-empty">Pro toto zboží nejsou v řadách 290/291 žádné odpisy.</p>
            )}
            {state.lines.length > 0 && (
              <div className="space-y-4">
                <Summary lines={state.lines} />
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#052E16]">Vývoj po měsících</h3>
                  <div className="inline-flex gap-1 p-1 bg-[#F1F5F1] rounded-xl">
                    {[{ id: 'value', label: 'Kč' }, { id: 'qty', label: 'Množství' }].map((m) => (
                      <button key={m.id} type="button" onClick={() => setMetric(m.id)} data-testid={`k2-hist-metric-${m.id}`}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${metric === m.id ? 'bg-white text-[#16A34A] shadow-sm' : 'text-[#57534E]'}`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="h-[240px]" data-testid="k2-hist-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart} stackOffset="sign" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} width={70} />
                      <Tooltip formatter={(v) => (metric === 'qty' ? fmtQty(v, unit) : fmtKc(v))} />
                      <Legend />
                      <Bar dataKey="b290" name="Řada 290" stackId="h" fill="#0EA5E9" />
                      <Bar dataKey="b291" name="Řada 291" stackId="h" fill="#8B5CF6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <SimpleTable testId="k2-hist-lines" columns={COLUMNS} rows={state.lines} rowKey={(r) => r.rid} />
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
